import React, { useCallback, useEffect, useState } from 'react';
import {
  Check, Copy, Loader2, PlugZap, Save, Sparkles, X,
} from 'lucide-react';
import client from '../lib/api';
import SecretInput from './components/SecretInput';
import { useToast } from '../hooks/use-toast';
import { GAME_LANGUAGES } from '../constants/gameLanguages';

const MASKED_SECRET = '••••••••••••••••';

// Sağlayıcı bildirimi bu adrese gelir ve adres **sağlayıcının kendi
// panelinde** durur; istekle gönderilmez. Bu yüzden aşağıdaki onay kutusu
// olmadan entegrasyon açılamaz (backend de 400 döner).
const CALLBACK_PATH = '/api/flexcasino/callback';

const DEFAULT_API_URL = 'https://api.upaflex.online';

// Sağlayıcının kabul ettiği diller. Ortak oyun dili listesinden dar olabilir;
// backend liste dışı değeri 400 ile geri çevirir, bu yüzden kesişim sunulur.
const LANGUAGES = ['tr', 'en', 'de', 'es', 'ru', 'pt', 'fr', 'it'];

const languageLabel = (code) => (
  GAME_LANGUAGES.find((item) => item.value === code)?.label || code.toUpperCase()
);

const hydrate = (config) => ({
  ...config,
  api_url: config.api_url || DEFAULT_API_URL,
  // Sır kaydedildikten sonra bir daha gösterilmez; alan yalnızca "kayıtlı mı"
  // bilgisini taşır.
  api_secret: config.api_secret_configured ? MASKED_SECRET : '',
});

const FlexCasinoSettingsPanel = ({ onClose, onSaved }) => {
  const [config, setConfig] = useState(null);
  const [callbackBase, setCallbackBase] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [probe, setProbe] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [configResult, settingsResult] = await Promise.allSettled([
      client.get('/admin/flexcasino/config'),
      client.get('/admin/settings'),
    ]);
    if (configResult.status === 'fulfilled') {
      setConfig(hydrate(configResult.value.data));
    } else {
      setError(
        configResult.reason?.response?.data?.detail
        || 'Flexrix Casino ayarları alınamadı',
      );
    }
    // Adres tabanı sunucudan gelir; tarayıcının origin'i kullanılamaz. Panel
    // ayrı bir alan adına taşındığında callback adresi panelin adresine
    // yazılırdı (bkz. backend/services/site_address.py).
    if (settingsResult.status === 'fulfilled') {
      const bases = settingsResult.value.data?.address_bases || {};
      setCallbackBase(bases.game || bases.site || '');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => {
    setConfig((current) => ({ ...current, [key]: value }));
  };

  const callbackUrl = `${callbackBase}${CALLBACK_PATH}`;

  const copyCallback = async () => {
    try {
      await navigator.clipboard.writeText(callbackUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };

  const save = async () => {
    const payload = {
      api_url: config.api_url,
      merchant_key: config.merchant_key || '',
      return_url: config.return_url || '',
      currency: config.currency || 'TRY',
      language: config.language || 'tr',
      enabled: !!config.enabled,
      slots_enabled: config.slots_enabled !== false,
      live_casino_enabled: config.live_casino_enabled !== false,
      demo_enabled: config.demo_enabled !== false,
      freespin_enabled: !!config.freespin_enabled,
      callback_registered: !!config.callback_registered,
    };
    // Maskeyi olduğu gibi geri göndermek kayıtlı secret'ı silerdi.
    if (config.api_secret && config.api_secret !== MASKED_SECRET) {
      payload.api_secret = config.api_secret;
    }

    setSaving(true);
    try {
      const { data } = await client.patch('/admin/flexcasino/config', payload);
      setConfig(hydrate(data));
      toast({ title: 'Flexrix Casino ayarları kaydedildi' });
      await onSaved?.();
    } catch (requestError) {
      toast({
        title: 'Ayarlar kaydedilemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await client.post('/admin/flexcasino/test');
      setProbe(data);
      toast({
        title: data.connected ? 'Bağlantı çalışıyor' : 'Bağlantı kurulamadı',
        description: data.connected
          ? `${data.provider_count} sağlayıcı listelendi`
          : data.error,
      });
    } catch (requestError) {
      toast({
        title: 'Bağlantı test edilemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="gc-state is-inline">
        <Loader2 size={20} className="gc-spin" />
        <p>Flexrix Casino ayarları yükleniyor…</p>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="gc-state">
        <X size={24} color="#fb7185" />
        <h2>Ayarlar yüklenemedi</h2>
        <p>{error || 'Flexrix Casino ayarları alınamadı.'}</p>
        <button type="button" onClick={onClose} className="gc-btn">Kapat</button>
      </div>
    );
  }

  const secretSaved = !!config.api_secret_configured;
  const ready = (
    !!String(config.merchant_key || '').trim()
    && (secretSaved || !!String(config.api_secret || '').trim())
  );

  return (
    <section className="gc-panel is-tone" style={{ '--gc-tone': '#f59e0b' }}>
      <div className="gc-panel-head">
        <span className="gc-card-icon"><Sparkles size={19} /></span>
        <div className="gc-panel-title">
          <h2>Flexrix Casino Ayarları</h2>
          <p>
            Native slot, masa ve crash oyunları. Cüzdan seamless çalışır: bakiye
            sitede kalır, sağlayıcı her bahis/kazancı tek bir callback adresine
            bildirir. İmza HMAC-SHA1’dir ve API secret hem giden isteklerin hem
            gelen bildirimlerin tek kanıtıdır.
          </p>
        </div>
        <div className="gc-badges">
          <span className={`gc-badge ${ready ? 'is-on' : 'is-warn'}`}>
            {ready ? 'Bilgiler tam' : 'Bilgi eksik'}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="gc-btn is-icon is-sm"
            aria-label="Ayarları kapat"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div className="gc-panel-body">
        <div className="gc-fields">
          <label className="gc-field is-full">
            <span className="gc-label">API URL</span>
            <input
              className="gc-input"
              value={config.api_url || ''}
              onChange={(event) => update('api_url', event.target.value)}
              placeholder={DEFAULT_API_URL}
            />
          </label>
          <label className="gc-field">
            <span className="gc-label">
              Merchant Key
              {!String(config.merchant_key || '').trim() && (
                <span className="gc-label-tag is-warn">zorunlu</span>
              )}
            </span>
            <input
              className="gc-input"
              value={config.merchant_key || ''}
              onChange={(event) => update('merchant_key', event.target.value)}
              placeholder="FXC_…"
              autoComplete="off"
            />
          </label>
          <label className="gc-field">
            <span className="gc-label">
              API Secret
              {secretSaved && <span className="gc-label-tag is-ok">kayıtlı</span>}
              {!secretSaved && <span className="gc-label-tag is-warn">zorunlu</span>}
            </span>
            <SecretInput
              value={config.api_secret || ''}
              onChange={(event) => update('api_secret', event.target.value)}
              onFocus={() => {
                if (config.api_secret === MASKED_SECRET) update('api_secret', '');
              }}
              placeholder={secretSaved ? 'Değiştirmek için yeni değer girin' : 'API Secret'}
            />
          </label>
          <label className="gc-field">
            <span className="gc-label">Para Birimi</span>
            <input
              className="gc-input"
              value={config.currency || 'TRY'}
              onChange={(event) => update('currency', event.target.value.toUpperCase())}
              placeholder="TRY"
            />
          </label>
          <label className="gc-field">
            <span className="gc-label">Oyun Dili</span>
            <select
              className="gc-input"
              value={config.language || 'tr'}
              onChange={(event) => update('language', event.target.value)}
            >
              {LANGUAGES.map((code) => (
                <option key={code} value={code}>{languageLabel(code)}</option>
              ))}
            </select>
          </label>
          <label className="gc-field is-full">
            <span className="gc-label">Oyundan Dönüş URL’si</span>
            <input
              className="gc-input"
              value={config.return_url || ''}
              onChange={(event) => update('return_url', event.target.value)}
              placeholder="https://siteniz.com/slotlar"
            />
          </label>
        </div>

        <div className="gc-box">
          <div className="gc-box-head">
            <div>
              <h3>Servisin yayınlanacağı bölümler</h3>
              <p>İşareti kaldırılan bölümde bu sağlayıcının oyunları görünmez.</p>
            </div>
          </div>
          <div className="gc-checks">
            <label className="gc-check is-plain">
              <input
                type="checkbox"
                checked={config.slots_enabled !== false}
                onChange={(event) => update('slots_enabled', event.target.checked)}
              />
              <span className="gc-check-text">Slot oyunları</span>
            </label>
            <label className="gc-check is-plain">
              <input
                type="checkbox"
                checked={config.live_casino_enabled !== false}
                onChange={(event) => update('live_casino_enabled', event.target.checked)}
              />
              <span className="gc-check-text">Canlı casino</span>
            </label>
            <label className="gc-check is-plain">
              <input
                type="checkbox"
                checked={config.demo_enabled !== false}
                onChange={(event) => update('demo_enabled', event.target.checked)}
              />
              <span className="gc-check-text">Demo (cüzdansız önizleme)</span>
            </label>
            <label className="gc-check is-plain">
              <input
                type="checkbox"
                checked={!!config.freespin_enabled}
                onChange={(event) => update('freespin_enabled', event.target.checked)}
              />
              <span className="gc-check-text">Serbest tur (freespin) kazancı kabul edilsin</span>
            </label>
          </div>
          <p className="gc-hint">
            Serbest turda sağlayıcı bahis göndermez, yalnızca kazancı bildirir.
            Kapı kapalıyken bahissiz kazanç reddedilir — sağlayıcı panelinden
            freespin kampanyası tanımlamıyorsanız kapalı bırakın.
          </p>
        </div>

        <div className="gc-box is-tone">
          <div className="gc-box-head">
            <div>
              <h3>Sağlayıcı paneline girilecek callback adresi</h3>
              <p>
                Bu adres sağlayıcının panelinde tanımlanır; istekle
                gönderilmez. Tanımlanmadan servis açılamaz.
              </p>
            </div>
          </div>
          <div className="gc-copy">
            <code className="gc-copy-url">{callbackUrl}</code>
            <button type="button" onClick={copyCallback} className="gc-btn is-sm">
              {copied ? <><Check size={12} /> Kopyalandı</> : <><Copy size={12} /> Kopyala</>}
            </button>
          </div>
          <div className="gc-checks">
            <label className="gc-check is-plain">
              <input
                type="checkbox"
                checked={!!config.callback_registered}
                onChange={(event) => update('callback_registered', event.target.checked)}
              />
              <span className="gc-check-text">
                Bu adresi sağlayıcı paneline tanımladım
              </span>
            </label>
          </div>
        </div>

        <div className="gc-box">
          <div className="gc-box-head">
            <div>
              <h3>Bağlantı testi</h3>
              <p>
                Sağlayıcı listesini imzalı olarak sorar; işlem üretmez. Demo ucu
                test olarak kullanılamaz — kimlik istemediği için anahtar
                yanlışken de cevap verir.
              </p>
            </div>
            <button
              type="button"
              onClick={test}
              disabled={testing}
              className="gc-btn is-sm"
            >
              {testing ? <Loader2 size={13} className="gc-spin" /> : <PlugZap size={13} />}
              Bağlantıyı Test Et
            </button>
          </div>
          {probe && (
            <p className={`gc-hint ${probe.connected ? '' : 'is-warn'}`}>
              {probe.connected
                ? `Bağlantı çalışıyor · ${probe.provider_count} sağlayıcı`
                : `${probe.error_code ? `${probe.error_code} · ` : ''}${probe.error}`}
            </p>
          )}
        </div>
      </div>

      <div className="gc-panel-foot">
        <span className="gc-foot-note">
          API secret kaydedildikten sonra bir daha gösterilmez; değiştirmek için
          yeni değer girin.
        </span>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="gc-btn is-primary"
        >
          {saving ? <Loader2 size={14} className="gc-spin" /> : <Save size={14} />}
          Ayarları Kaydet
        </button>
      </div>
    </section>
  );
};

export default FlexCasinoSettingsPanel;
